import '@lockton-companies/locktonre-shared-web-components/chat-window'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  // HostListener,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ClaudeToolDef,
  HandlerResult,
  IntegratedChatClient,
  ThreadMessage,
  defineConfig,
} from '@lockton-companies/locktonre-shared-web-components/integrated-chat'
import { ResizableModule, ResizeEvent } from 'angular-resizable-element'
import { Store, select } from '@ngrx/store'
import { combineLatest, filter, firstValueFrom, Subscription } from 'rxjs'
import { AppState } from '../../core/store'
import { selectAuthState, selectUserName } from '../../core/store/auth/auth.selectors'
import {
  selectCurrentClient,
  selectCurrentStudy,
  selectCurrentYear,
} from '../../core/store/broker/broker.selectors'
import { selectRouterStateUrl } from '../../core/store/router.selectors'
import { environment } from '../../../environments/environment'
import { sageTheme } from '../../chat/chat.theme'
import { AuthService } from '../../auth.service'
import { IngestionReviewActions } from '../../quote/ingestion-review/store/ingestion-review.actions'
import { QuoteIngestionStatus } from '../../quote/ingestion-review/models/ingestion-review.model'
import { resetAnalysis } from '../../analysis/store/analysis.actions'
import {
  selectEditorPortfolioSetID,
  selectCededLayers,
  selectLossSetLayersDirty,
  selectTechnicalPremiumCanSave,
  selectMetricTableSettingsEntities,
  selectMetricTableSettingsStudyID,
} from '../../analysis/store/analysis.selectors'
import { fetchMetricTableSettings } from '../../analysis/store/metrics/metrics-cart/metrics-cart.actions'

export type ChatTool = ClaudeToolDef & {
  handler: (input: Record<string, unknown>) => HandlerResult
}

/** Greeting body — also the marker greetIfNeeded uses to detect a greeting already on screen. */
const GREETING_MARKER = "I'm your SAGE AI assistant. What would you like to work on today?"

@Component({
  selector: 'app-chat-window-overlay',
  standalone: true,
  imports: [ResizableModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-window-overlay.component.html',
  styleUrl: './chat-window-overlay.component.scss',
})
export class ChatWindowOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  private chatEl = viewChild.required<ElementRef<HTMLElement>>('chatEl')
  private el = inject(ElementRef)
  private store = inject<Store<AppState>>(Store)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private authService = inject(AuthService)

  title = input('AI Assistant')
  tools = input<ChatTool[]>([])
  mcpServers = input<string[]>(['sage'])
  additionalContext = input<Record<string, string | undefined>>({})

  isOpen = signal(false)
  panelWidth = signal(400)
  panelHeight = signal(580)

  private client: IntegratedChatClient | null = null
  private contextSnapshot: Record<string, string | undefined> = {}
  private contextSub: Subscription | null = null
  private keyboardListeners: (() => void)[] = []
  private deepLinkSub: Subscription | null = null
  private deepLinkHandled = false
  private _pendingIngestions = new Map<string, { url: string; matched: boolean }>()
  private sharedThread: { threadId: string; history: ThreadMessage[] } | null = null
  private sharedBinding: Promise<void> | null = null
  private sharedBindingEpoch = 0
  private heldSends: Array<{
    message: string
    attachments: Array<{ file: File; name: string; size: number }>
    holdId?: string
  }> = []
  private _lastIngestedEmailId: number | null = null

  private static readonly THREAD_STORAGE_PREFIX = 'sage-chat-thread-id'
  private static readonly CHAT_OPEN_PREFIX = 'sage-chat-open'

  private get threadStorageKey(): string {
    const user = this.contextSnapshot['user'] ?? 'anonymous'
    return `${ChatWindowOverlayComponent.THREAD_STORAGE_PREFIX}:${user}`
  }

  private get chatOpenKey(): string {
    const user = this.contextSnapshot['user'] ?? 'anonymous'
    return `${ChatWindowOverlayComponent.CHAT_OPEN_PREFIX}:${user}`
  }

  private static readonly QUOTE_KEYWORDS = [
    'quote response',
    'written line',
    'ceding commission',
    'brokerage',
    'rol',
    'epi',
    'quota share',
    'excess of loss',
    'reinstatement',
  ]

  private static readonly QUOTE_KEYWORD_THRESHOLD = 3

  private static readonly STRUCTURE_ID_PATTERNS = [
    /structure\s+id\s*[:\-]?\s*(\d+)/i,
    /structureid\s*[:\-]?\s*(\d+)/i,
    /structure\s*[:\-]?\s*(\d+)/i,
  ]

  private readonly MIN_WIDTH = 320
  private readonly MIN_HEIGHT = 400
  private get maxWidth() {
    return window.innerWidth - 16
  }
  private get maxHeight() {
    return window.innerHeight - 50
  }

  onResize(event: ResizeEvent): void {
    const w = event.rectangle.width ?? this.panelWidth()
    const h = event.rectangle.height ?? this.panelHeight()
    this.panelWidth.set(Math.min(this.maxWidth, Math.max(this.MIN_WIDTH, w)))
    this.panelHeight.set(Math.min(this.maxHeight, Math.max(this.MIN_HEIGHT, h)))
  }

  ngOnInit(): void {
    document.body.appendChild(this.el.nativeElement)

    this.contextSub = combineLatest([
      this.store.pipe(select(selectUserName)),
      this.store.pipe(select(selectCurrentClient)),
      this.store.pipe(select(selectCurrentYear)),
      this.store.pipe(select(selectCurrentStudy)),
      this.store.pipe(select(selectRouterStateUrl)),
    ]).subscribe(([username, client, year, study, page]) => {
      const urlParams = new URLSearchParams(window.location.search)
      const openEmailId = urlParams.get('openEmailId')
      this.contextSnapshot = {
        user: username ?? undefined,
        client: client?.name ?? undefined,
        clientId: client?.id ?? undefined,
        year: year?.year ?? undefined,
        yearId: year?.id ?? undefined,
        draft: study?.name ?? undefined,
        studyId: study?.id ?? undefined,
        page: page ?? undefined,
        route: this.draftRouteFromUrl(page) ?? undefined,
        openEmailId: openEmailId ?? this._lastIngestedEmailId?.toString() ?? undefined,
      }
    })

    // Prevent Shadow DOM keyboard events from triggering SAGE global shortcuts
    // while the user is typing in the chat panel. Listen in the bubble phase on
    // the host element so that the event first propagates through the shadow DOM
    // (letting the chat-window handle Enter-to-send, etc.) and is then stopped
    // before reaching app-level listeners.
    const host = this.el.nativeElement as HTMLElement
    const stop = (e: KeyboardEvent) => {
      if (!this.isOpen() || e.ctrlKey || e.metaKey || e.altKey) return
      e.stopPropagation()
    }
    for (const type of ['keydown', 'keyup', 'keypress'] as const) {
      host.addEventListener(type, stop)
      this.keyboardListeners.push(() => host.removeEventListener(type, stop))
    }
  }

  ngAfterViewInit(): void {
    const config = defineConfig({
      appId: 'sage-core',
      harnessUrl: environment.chatApi.base,
      getAuthToken: () =>
        firstValueFrom(
          this.store.pipe(
            select(selectAuthState),
            filter((s) => !!s.token),
          ),
        ).then((s) => s.token as string),
      getContext: () => ({
        ...this.contextSnapshot,
        ...this.additionalContext(),
        screenContent: this.extractScreenContent(),
      }),
      manifest: {
        displayName: this.title(),
        tools: [...this.tools(), ...this.buildInternalTools()],
        mcpServers: this.mcpServers(),
        systemPromptContext:
          'FORMAT: Use markdown tables for structured data (comparisons, lists of items with attributes, results). ' +
          'Use bullet lists for steps or options. Keep prose short — 2–3 sentences max. ' +
          'Never use headers inside a chat response. Skip preamble, answer directly.\n\n' +
          'FILES: When the user attaches Excel/CSV files, they are staged in Azure Blob Storage and their ' +
          'names + blob refs appear in an [ATTACHED FILES] block inside the user message.\n' +
          "ROUTING: Route attached Excel/CSV files by the user's explicit intent only. Intent means one " +
          'clear, affirmative instruction to run a specific pipeline ("ingest this quote", "run the ' +
          'submission builder", or a direct answer to your routing question naming exactly one pipeline). ' +
          'A question ("is this a quote or a submission?"), a negation ("don\'t ingest this"), a bare ' +
          'mention of a pipeline word, or a message naming both pipelines is NOT intent — treat all of ' +
          'those as NO stated intent.\n' +
          '- QUOTE intent: call sage__sage_quote_ingestion once per file with blobRef and fileName. The tool ' +
          'returns EMAIL_ID:<id> on success; end your reply with the markdown link ' +
          '[Review in AI Quote Ingestion](/quote/ai-quote-ingestion?openEmailId=<id>) using the last ' +
          'ingested id — never show the raw EMAIL_ID sentinel to the user.\n' +
          '- SUBMISSION intent: call sage__submission_builder ONCE with every blob ref in file_refs — never one call ' +
          'per file, and never call it more than once in the same response. ' +
          "The pipeline detects each file's type (claims/policy/development) during validation — " +
          'do not ask the user what type a file is before calling the submission tool.\n' +
          '- NO stated intent: confirm the files were received (list them), then ask whether to run the ' +
          'submission builder or quote ingestion, and call NEITHER tool until the user answers. File-name ' +
          'hints (market/reinsurer names e.g. "HCC", "Canopius", "RenRe", "Swiss Re", "Munich Re", "quote", ' +
          '"structure", "pricing", "terms" → likely quote; "claims", "policy", "exposure", "schedule", "SOV", ' +
          '"loss run", "triangle", "bordereaux" → likely submission) and being on the AI Quote Ingestion page ' +
          '(/quote/ai-quote-ingestion) may shape the recommendation inside your question, never skip it.\n' +
          'A pipeline choice covers only the files staged when it was given — newly attached files need ' +
          'their own instruction or answer.\n' +
          'PDF attachments are readable directly in the conversation: extract the tabular data yourself, ' +
          "then follow the submission builder's instructions for staging extracted data. " +
          'Never ask the user to re-upload or provide paths manually. ' +
          'When the pipeline needs an ADDITIONAL file (e.g. a claims file to go with a policy/vehicle ' +
          'schedule), end your turn with a plain-text request to attach it — never via ' +
          'uichat_present_action_buttons, because the attach control is locked while your turn is running. ' +
          'The user can also send a file with no message text; that is a NO-stated-intent upload — ' +
          'follow the ROUTING rules and ask which pipeline to run.\n\n' +
          'RESULTS: Brokers always want to see the in-between stages, not just final totals. When a ' +
          'submission run completes, walk through the stage_report returned by the tool stage by stage ' +
          '(Stacking, LOB assignment, Aggregation, Exhibit generation), each as a short markdown table — ' +
          'stacking method and reconciliation totals, the full LOB distribution, aggregation grouping and ' +
          'row counts, exhibit contents — then warnings, then the download link.\n\n' +
          'QUOTE EDITING: If openEmailId is in context, use sage__sage_quote_edit for field edits ' +
          '(emailId = openEmailId). Do NOT edit Client, Program, Structure, or Market — those ' +
          'require the manual dropdowns on the quote card. Call sage_refresh_ingestion only on ' +
          'the ingestion page. After the edit, call uichat_show_toast for each edit: variant ' +
          '"success" with message "✓ Field → value" or variant "error" with "✗ Field — reason". ' +
          'Do not write a text response about the edit results — the toasts are sufficient. ' +
          'Never repeat the same question or information in multiple messages.',
      },
      attachments: {
        enabled: true,
        accept: ['.msg', '.eml', '.xlsx', '.xls', '.xlsm', '.xlsb', '.csv', '.pdf'],
      },
    })

    // Intercept .msg/.eml uploads before IntegratedChatClient routes them to the
    // integrated-chat backend — email files go to the quote ingestion endpoint instead.
    // Also validate that all attachments have accepted extensions.
    const chatElement = this.chatEl().nativeElement
    const acceptedExts = new Set(['msg', 'eml', 'xlsx', 'xls', 'xlsm', 'xlsb', 'csv', 'pdf'])
    const MAX_FILE_SIZE = 150 * 1024 * 1024 // 150 MB
    chatElement.addEventListener(
      'chat-send',
      (e: Event) => {
        const detail = (e as CustomEvent).detail
        const attachments: Array<{ file: File; name: string; size: number }> =
          detail?.attachments ?? []
        const message: string = detail?.message ?? ''

        if (this.sharedBinding) {
          e.stopImmediatePropagation()
          if (!message.trim() && attachments.length === 0) return
          const holdId = this.client?.controller.addIndeterminateMessage(
            'Connecting to this conversation...',
          )
          this.heldSends.push({ message, attachments, holdId })
          if (this.heldSends.length === 1) {
            const epoch = this.sharedBindingEpoch
            void this.sharedBinding.then(() => this.replayHeldSends(epoch))
          }
          return
        }

        if (this.sharedThread) {
          e.stopImmediatePropagation()
          if (message.trim() || attachments.length > 0) {
            void this.continueSharedThread(message, attachments)
          }
          return
        }

        // Intercept pasted quote text (no attachments).
        // Skip if a conversation thread is already active — the message is a
        // follow-up response, not a fresh quote paste.
        if (attachments.length === 0) {
          const hasActiveThread = !!this.client?.threadId
          if (message && !hasActiveThread && this.looksLikeQuote(message)) {
            e.stopImmediatePropagation()
            void this.handleQuoteTextIngestion(message)
          }
          return
        }

        // Validate all attachments — block unsupported or oversized files
        for (const a of attachments) {
          const ext = a.name.toLowerCase().split('.').pop() ?? ''
          if (!acceptedExts.has(ext)) {
            e.stopImmediatePropagation()
            this.client?.controller.addUserMessage(detail?.message || a.name)
            this.client?.controller.addErrorMessage(
              `"${a.name}" is not a supported file type. Accepted types: ${[...acceptedExts].map((x) => '.' + x).join(', ')}`,
            )
            return
          }
          if (a.size === 0) {
            e.stopImmediatePropagation()
            this.client?.controller.addUserMessage(detail?.message || a.name)
            this.client?.controller.addErrorMessage(
              `"${a.name}" is empty. Please attach a valid file.`,
            )
            return
          }
          if (a.size > MAX_FILE_SIZE) {
            e.stopImmediatePropagation()
            this.client?.controller.addUserMessage(detail?.message || a.name)
            this.client?.controller.addErrorMessage(`"${a.name}" exceeds the 150 MB size limit.`)
            return
          }
        }

        // Route email files (.msg/.eml) directly to quote ingestion — always.
        // When emails are present in a mixed batch (e.g. .msg + .xlsx), send
        // ALL attachments through quote ingestion so nothing is silently
        // dropped. handleEmailIngestion already validates each file's extension
        // individually and shows clear errors for unsupported types.
        const hasEmailFiles = attachments.some((a: { name: string }) => {
          const name = a.name.toLowerCase()
          return name.endsWith('.msg') || name.endsWith('.eml')
        })
        if (hasEmailFiles) {
          e.stopImmediatePropagation()
          this.handleEmailIngestion(detail?.message ?? '', attachments)
          return
        }
      },
      { capture: true },
    )

    chatElement.addEventListener(
      'click',
      (e: Event) => {
        const mouse = e as MouseEvent
        if (
          mouse.button !== 0 ||
          mouse.ctrlKey ||
          mouse.metaKey ||
          mouse.shiftKey ||
          mouse.altKey
        ) {
          return
        }
        const anchor = (e.composedPath?.() ?? []).find(
          (el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement && !!el.href,
        )
        if (!anchor) return
        let url: URL
        try {
          url = new URL(anchor.href, window.location.href)
        } catch {
          return
        }

        if (url.protocol === 'mailto:' || url.protocol === 'tel:') return
        e.preventDefault()
        e.stopPropagation()
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return
        const isFileLink =
          /\.(xlsx|xlsm|xlsb|xls|csv|pdf|zip|docx|pptx)([?#]|$)/i.test(url.pathname) ||
          /[?&]sig=/.test(url.search) || // Azure SAS URL
          url.hostname.endsWith('blob.core.windows.net')
        if (isFileLink) {
          const frame = document.createElement('iframe')
          frame.style.display = 'none'
          frame.src = url.toString()
          frame.referrerPolicy = 'no-referrer'
          frame.sandbox.add('allow-downloads')
          document.body.appendChild(frame)
          setTimeout(() => frame.remove(), 60_000)
        } else if (url.origin === window.location.origin) {
          void this.router.navigateByUrl(url.pathname + url.search + url.hash)
        } else {
          window.open(url.toString(), '_blank', 'noopener,noreferrer')
        }
      },
      { capture: true },
    )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.client = new IntegratedChatClient(chatElement as any, config)
    this.client.controller.setTitle(this.title())
    this.client.controller.setShowClearButton(true)
    this.client.controller.setTheme(sageTheme)
    this.reviveLateChoiceClicks()

    this.client.controller.onClear(() => {
      void this.startFreshThread()
    })

    this.setupShareLink()
    this.watchForSharedThreadLink()
    void this.restorePreviousThread()

    chatElement.addEventListener('chat-send', () => {
      setTimeout(() => this.persistCurrentThread(), 2000)
    })
  }

  private buildInternalTools(): ChatTool[] {
    return [
      {
        name: 'sage_refresh_ingestion',
        description:
          'Reload the AI Quote Ingestion list in the SAGE UI. ' +
          'Call this after sage-quote-edit succeeds so the user sees the updated values without refreshing.',
        input_schema: { type: 'object' as const, properties: {} },
        handler: async () => {
          const isOnIngestionPage = window.location.pathname.includes('/quote/ai-quote-ingestion')
          if (!isOnIngestionPage) {
            return { ok: true, message: 'Not on ingestion page — no refresh needed.' }
          }
          this.store.dispatch(IngestionReviewActions.fetchIngestions())
          return { ok: true }
        },
      },
      {
        name: 'sage_reload_structure',
        description:
          'Reload the current structure in the SAGE UI after creating, editing, or deleting layers. ' +
          'Call this after any successful layer mutation so the user sees the changes without refreshing.',
        input_schema: { type: 'object' as const, properties: {} },
        handler: async () => {
          const portfolioSet = await firstValueFrom(
            this.store.pipe(select(selectEditorPortfolioSetID)),
          )
          if (!portfolioSet?.cededPortfolioID) {
            return { ok: false, error: 'No active structure to reload' }
          }
          try {
            const [layers, lossSetDirty] = await Promise.all([
              firstValueFrom(this.store.pipe(select(selectCededLayers))),
              firstValueFrom(this.store.pipe(select(selectLossSetLayersDirty))),
            ])
            const hasDirty =
              lossSetDirty || layers.filter((l) => l.deleted || l.dirty || l.new).length > 0
            if (hasDirty) {
              return {
                ok: false,
                error:
                  'Cannot reload — the user has unsaved UI changes that would be lost. ' +
                  'Tell the user to save first.',
              }
            }
          } catch {
            // Store not initialized — safe to reload
          }
          this.store.dispatch(resetAnalysis({ keepPanelOpen: false }))
          return { ok: true }
        },
      },
      {
        name: 'sage_reload_metrics',
        description:
          'Reload the Metrics page (program metric settings) in the SAGE UI from the server. ' +
          'Call this after any successful sage-metric-settings change so the user sees it without ' +
          'refreshing, and so a later Save on the page does not overwrite the change with stale values.',
        input_schema: { type: 'object' as const, properties: {} },
        handler: async () => {
          let studyID: string | null | undefined
          try {
            studyID = await firstValueFrom(
              this.store.pipe(select(selectMetricTableSettingsStudyID)),
            )
          } catch {
            // Analysis feature state not initialised yet (module not loaded) — nothing to reload
            studyID = undefined
          }
          if (!studyID) {
            return { ok: true, message: 'Metric settings are not loaded — no refresh needed.' }
          }
          if (await this.hasUnsavedMetricChanges()) {
            return {
              ok: false,
              error:
                'Cannot reload — the user has unsaved changes on the Metrics page that would be lost. ' +
                'Tell the user to save or discard them first.',
            }
          }
          // Replaces the page's metric list (fetchMetricTableSettingsSuccess → setAll)
          this.store.dispatch(fetchMetricTableSettings({ studyID }))
          return { ok: true }
        },
      },
      {
        name: 'sage_check_unsaved_changes',
        description:
          'IMPORTANT: Call this BEFORE any layer creation, editing, or deletion via sage-layer-design ' +
          'or sage-layer-edit, and BEFORE any sage-metric-settings change. Checks if the user has ' +
          'unsaved design or Metrics page changes in the SAGE UI. ' +
          'If unsaved changes exist, do NOT proceed with the mutation — tell the user to save first.',
        input_schema: { type: 'object' as const, properties: {} },
        handler: async () => {
          try {
            const designDirty = await this.hasUnsavedDesignChanges()
            if (await this.hasUnsavedMetricChanges()) {
              return {
                ok: false,
                hasUnsavedChanges: true,
                error:
                  'The user has unsaved changes on the Metrics page. ' +
                  'Tell the user to save or discard them before changing metric settings via chat, ' +
                  'otherwise saving the page would overwrite the chat changes. Do NOT proceed.',
              }
            }
            if (designDirty) {
              return {
                ok: false,
                hasUnsavedChanges: true,
                error:
                  'The user has unsaved design changes in the SAGE UI. ' +
                  'Tell the user to save their changes in the UI before making edits via chat. ' +
                  'Do NOT proceed with the layer mutation.',
              }
            }
            return { ok: true, hasUnsavedChanges: false }
          } catch {
            return { ok: true, hasUnsavedChanges: false }
          }
        },
      },
      {
        name: 'sage_navigate_to_structure',
        description:
          'Navigate to a structure in the SAGE UI. Call this after creating or cloning a structure ' +
          'when the user wants to view the new structure. Requires structureId, analysisProfileId, ' +
          'cededPortfolioId, grossPortfolioId, and netPortfolioId.',
        input_schema: {
          type: 'object' as const,
          properties: {
            structureId: {
              type: 'string',
              description: 'The SAGE structure ID (numeric, as string).',
            },
            structureName: {
              type: 'string',
              description: 'The structure name to display in the navigation message.',
            },
            analysisProfileId: {
              type: 'string',
              description: 'The ARE analysis profile ID.',
            },
            cededPortfolioId: {
              type: 'string',
              description: 'The ARE ceded portfolio ID.',
            },
            grossPortfolioId: {
              type: 'string',
              description: 'The ARE gross portfolio ID.',
            },
            netPortfolioId: {
              type: 'string',
              description: 'The ARE net portfolio ID.',
            },
            parentGrossPortfolioId: {
              type: 'string',
              description: 'The ARE parent gross portfolio ID.',
            },
          },
          required: [
            'structureId',
            'analysisProfileId',
            'cededPortfolioId',
            'grossPortfolioId',
            'netPortfolioId',
          ],
        },
        handler: async (input: Record<string, unknown>) => {
          // Structures live under /clients/:client/years/:year/programs/:program — this works
          // from the structure list as well as from an open structure (TIER_ROUTES)
          const brokerPrefix = this.router.url.match(
            /^(.*?\/clients\/[^/?#]+\/years\/[^/?#]+\/programs\/[^/?#]+)/,
          )?.[1]
          if (!brokerPrefix) {
            return {
              ok: false,
              error:
                'Cannot navigate — no program is open. Tell the user the structure was created and ' +
                'they can open it from the program’s structure list.',
            }
          }

          // The full reload below bypasses Angular's CanDeactivate guard, so never discard
          // unsaved work — let the user save first
          if ((await this.hasUnsavedDesignChanges()) || (await this.hasUnsavedMetricChanges())) {
            return {
              ok: false,
              hasUnsavedChanges: true,
              error:
                'Not navigating — the user has unsaved changes on this page that a reload would lose. ' +
                'Tell the user the structure was created, ask them to save (or discard) their changes, ' +
                'then offer to open the new structure.',
            }
          }

          const portfolioIDsObj: Record<string, unknown> = {
            cededPortfolioID: input['cededPortfolioId'],
            grossPortfolioID: input['grossPortfolioId'],
            netPortfolioID: input['netPortfolioId'],
          }
          if (input['parentGrossPortfolioId']) {
            portfolioIDsObj['parentGrossPortfolioID'] = input['parentGrossPortfolioId']
          }
          const portfolioIDs = encodeURIComponent(JSON.stringify(portfolioIDsObj))

          const newUrl =
            `${brokerPrefix}/structure/${input['structureId']}` +
            `/analysis/${input['analysisProfileId']}` +
            `/portfolios/${portfolioIDs}`

          const structureName =
            (input['structureName'] as string) || `Structure ${input['structureId']}`
          this.client?.controller.addAgentMessage(`Navigating to **${structureName}**...`)

          localStorage.setItem(this.threadStorageKey, this.client?.threadId ?? '')
          if (this.isOpen()) {
            localStorage.setItem(this.chatOpenKey, 'true')
          }
          setTimeout(() => {
            window.location.href = newUrl
          }, 500)

          return { ok: true, message: `Navigating to ${structureName}` }
        },
      },
    ]
  }

  // Unsaved tower/design work: layers, loss sets, technical premium
  private async hasUnsavedDesignChanges(): Promise<boolean> {
    try {
      const [layers, lossSetDirty, techPremiumCanSave] = await Promise.all([
        firstValueFrom(this.store.pipe(select(selectCededLayers))),
        firstValueFrom(this.store.pipe(select(selectLossSetLayersDirty))),
        firstValueFrom(this.store.pipe(select(selectTechnicalPremiumCanSave))),
      ])
      return (
        lossSetDirty ||
        techPremiumCanSave ||
        layers.filter((l) => l.deleted || l.dirty || l.new).length > 0
      )
    } catch {
      // Analysis feature state not initialised — nothing unsaved
      return false
    }
  }

  // Same signal as the Metrics page Save button (metrics.container metricDirty$)
  private async hasUnsavedMetricChanges(): Promise<boolean> {
    try {
      const metrics = await firstValueFrom(
        this.store.pipe(select(selectMetricTableSettingsEntities)),
      )
      return metrics.some((m) => m.dirty)
    } catch {
      return false
    }
  }

  private async restorePreviousThread(): Promise<void> {
    if (!this.client || this.deepLinkHandled) return

    const client = this.client
    const savedThreadId = localStorage.getItem(this.threadStorageKey)
    if (savedThreadId) {
      // Registered as the in-flight binding so Clear (startFreshThread) waits for it and bumps
      // the epoch; a stale restore must not rebind or re-render the old thread after Clear.
      // Never rejects — startFreshThread awaits it.
      const epoch = this.sharedBindingEpoch
      const restore = (async () => {
        try {
          const history = await client.getThreadMessages({ threadId: savedThreadId, limit: 100 })
          if (epoch !== this.sharedBindingEpoch) return
          await client.loadThread(savedThreadId)
          if (epoch !== this.sharedBindingEpoch) return
          this.renderThreadHistory(history)
        } catch {
          if (epoch === this.sharedBindingEpoch) localStorage.removeItem(this.threadStorageKey)
        }
      })()
      this.sharedBinding = restore
      try {
        await restore
      } finally {
        if (this.sharedBinding === restore) this.sharedBinding = null
      }
      if (epoch !== this.sharedBindingEpoch) return
    }

    const wasOpen = localStorage.getItem(this.chatOpenKey)
    if (wasOpen === 'true') {
      this.isOpen.set(true)
      localStorage.removeItem(this.chatOpenKey)
    }

    this.persistCurrentThread()
  }

  private persistCurrentThread(): void {
    const threadId = this.client?.threadId
    if (threadId) {
      localStorage.setItem(this.threadStorageKey, threadId)
    }
  }

  private setupShareLink(): void {
    const controller = this.client!.controller
    controller.addHeaderButton('share-thread-link', this.buildShareIcon(), 'Copy conversation link')
    controller.onHeaderButtonClick((buttonId) => {
      if (buttonId === 'share-thread-link') void this.copyShareLink()
    })
  }

  private buildShareIcon(): Element {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('width', '16')
    svg.setAttribute('height', '16')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', 'currentColor')
    svg.setAttribute('stroke-width', '2')
    svg.setAttribute('stroke-linecap', 'round')
    svg.setAttribute('stroke-linejoin', 'round')
    const upper = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    upper.setAttribute('d', 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71')
    const lower = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    lower.setAttribute('d', 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71')
    svg.append(upper, lower)
    return svg
  }

  private async copyShareLink(): Promise<void> {
    const controller = this.client!.controller
    const threadId = this.sharedThread?.threadId ?? this.client!.threadId
    if (!threadId) {
      controller.addSystemMessage(
        'The conversation link becomes available once the conversation has started.',
      )
      return
    }
    const url = `${window.location.origin}/?chatThread=${encodeURIComponent(threadId)}`
    try {
      await navigator.clipboard.writeText(url)
      controller.addSystemMessage('Conversation link copied to clipboard.')
    } catch {
      controller.addSystemMessage(`Conversation link: ${url}`)
    }
  }

  private watchForSharedThreadLink(): void {
    const stored = sessionStorage.getItem('sage.chatThread')
    if (stored) {
      sessionStorage.removeItem('sage.chatThread')
      this.deepLinkHandled = true
      void this.openSharedThread(stored).finally(() => this.clearShareLinkParam())
      return
    }
    this.deepLinkSub = this.route.queryParamMap.subscribe((params) => {
      const threadId = params.get('chatThread')
      if (!threadId || this.deepLinkHandled) return
      this.deepLinkHandled = true
      void this.openSharedThread(threadId).finally(() => this.clearShareLinkParam())
    })
  }

  private clearShareLinkParam(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { chatThread: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  private async openSharedThread(threadId: string): Promise<void> {
    const client = this.client
    if (!client) return
    const epoch = ++this.sharedBindingEpoch
    const controller = client.controller
    this.isOpen.set(true)
    controller.setDisabled(true)
    const loadingId = controller.addIndeterminateMessage('Loading shared conversation...')

    let history: ThreadMessage[]
    try {
      history = await client.getThreadMessages({ threadId, limit: 100 })
    } catch {
      controller.removeMessage(loadingId)
      if (epoch !== this.sharedBindingEpoch) return
      controller.addErrorMessage(
        'This conversation could not be found, or you do not have access to it.',
      )
      controller.setDisabled(false)
      return
    }

    controller.removeMessage(loadingId)
    if (epoch !== this.sharedBindingEpoch) return
    this.renderThreadHistory(history)
    controller.setDisabled(false)
    const binding = (async () => {
      await this.settleInitialConnect(client)
      if (epoch !== this.sharedBindingEpoch) return
      const bound = client.loadThread(threadId).catch((): undefined => undefined)
      this.renderThreadHistory(history)
      await bound
      if (epoch !== this.sharedBindingEpoch) return
      if (client.threadId !== threadId) {
        this.enterSharedReadOnlyView(threadId, history)
      }
    })()
    this.sharedBinding = binding
    try {
      await binding
    } finally {
      this.sharedBinding = null
    }
  }

  private enterSharedReadOnlyView(threadId: string, history: ThreadMessage[]): void {
    const controller = this.client!.controller
    this.sharedThread = { threadId, history }
    controller.addSystemMessage(
      "You're viewing a shared conversation. The original stays read-only — sending a message continues it as your own copy.",
    )
    controller.setDisabled(false)
  }

  private async replayHeldSends(epoch: number): Promise<void> {
    while (this.heldSends.length > 0) {
      const { message, attachments, holdId } = this.heldSends.shift()!
      if (holdId) this.client?.controller.removeMessage(holdId)
      if (epoch !== this.sharedBindingEpoch) continue
      if (this.sharedThread) {
        await this.continueSharedThread(message, attachments)
      } else {
        this.chatEl().nativeElement.dispatchEvent(
          new CustomEvent('chat-send', { detail: { message, attachments } }),
        )
      }
    }
  }

  private async continueSharedThread(
    message: string,
    attachments: Array<{ file: File; name: string; size: number }>,
  ): Promise<void> {
    const client = this.client
    const shared = this.sharedThread
    if (!client || !shared) return
    const controller = client.controller
    const epoch = this.sharedBindingEpoch
    controller.setDisabled(true)
    const loadingId = controller.addIndeterminateMessage(
      'Creating your copy of this conversation...',
    )

    // Registered as the in-flight binding so Clear waits for it; after Clear (epoch bump) the
    // copy is neither bound nor sent to. Never rejects — startFreshThread awaits it.
    const continuation = (async (): Promise<string | Error | null> => {
      try {
        const token = await this.chatAuthToken()
        const response = await fetch(
          `${environment.chatApi.base}/v1/threads/${encodeURIComponent(shared.threadId)}/continue`,
          { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
        )
        if (!response.ok) {
          throw new Error(`Continue failed (${response.status})`)
        }
        const { threadId } = (await response.json()) as { threadId: string }
        if (epoch !== this.sharedBindingEpoch) return null
        await client.loadThread(threadId)
        return threadId
      } catch (err: unknown) {
        return err instanceof Error ? err : new Error(String(err))
      }
    })()
    const binding = continuation.then((): void => undefined)
    this.sharedBinding = binding
    let result: string | Error | null
    try {
      result = await continuation
    } finally {
      if (this.sharedBinding === binding) this.sharedBinding = null
    }
    if (epoch !== this.sharedBindingEpoch) {
      // Cleared meanwhile — startFreshThread owns the conversation and the controller state
      controller.removeMessage(loadingId)
      return
    }

    try {
      if (result === null || result instanceof Error) {
        throw result ?? new Error('Continue cancelled')
      }
      const threadId = result
      if (client.threadId !== threadId) {
        throw new Error('Thread binding failed')
      }
      this.sharedThread = null
      controller.removeMessage(loadingId)
      this.renderThreadHistory(shared.history)
      this.persistCurrentThread()
      if (attachments.length > 0) {
        controller.setDisabled(false)
        this.chatEl().nativeElement.dispatchEvent(
          new CustomEvent('chat-send', { detail: { message, attachments } }),
        )
      } else {
        this.sendAsUserMessage(message)
      }
    } catch {
      controller.removeMessage(loadingId)
      controller.addErrorMessage('Unable to continue this conversation. Please try again.')
      controller.setDisabled(false)
    }
  }

  private async chatAuthToken(): Promise<string> {
    const state = await firstValueFrom(
      this.store.pipe(
        select(selectAuthState),
        filter((s) => !!s.token),
      ),
    )
    return state.token as string
  }

  private async settleInitialConnect(client: IntegratedChatClient): Promise<void> {
    const pending = (client as unknown as { connectPromise?: Promise<void> }).connectPromise
    if (pending) await pending.catch((): undefined => undefined)
  }

  private renderThreadHistory(messages: ThreadMessage[]): void {
    const controller = this.client!.controller
    for (const message of messages) {
      const text = this.extractMessageText(message.content)
      if (!text) continue
      const timestamp = message.createdAt ? new Date(message.createdAt) : undefined
      if (message.role === 'user') {
        controller.addUserMessage(text, undefined, timestamp)
      } else {
        controller.addAgentMessage(text, timestamp)
      }
    }
  }

  private extractMessageText(content: unknown): string {
    if (typeof content === 'string') return content.trim()
    if (!Array.isArray(content)) return ''
    const parts: string[] = []
    for (const block of content as Array<{ type?: string; text?: string }>) {
      if (block?.type !== 'text' || typeof block.text !== 'string') continue
      if (block.text.startsWith('--- BEGIN UI CONTEXT ---')) continue
      if (block.text.startsWith('[ATTACHED FILES')) continue
      parts.push(block.text)
    }
    return parts.join('\n').trim()
  }

  private async startFreshThread(): Promise<void> {
    const client = this.client
    if (!client) return
    const controller = client.controller

    this.sharedThread = null
    this.sharedBindingEpoch++
    this.heldSends = []
    const pendingBinding = this.sharedBinding
    this.sharedBinding = null
    localStorage.removeItem(this.threadStorageKey)
    controller.setDisabled(true)
    controller.setShowInterrupt(false)
    try {
      if (pendingBinding) await pendingBinding
      await client.startNewThread()
      this.persistCurrentThread()
    } catch (err: unknown) {
      controller.addErrorMessage(err instanceof Error ? err.message : String(err))
      return
    } finally {
      controller.setDisabled(false)
    }
    this.greetIfNeeded()
  }

  private reviveLateChoiceClicks(): void {
    this.client!.controller.onAction((messageId, actionId) => {
      // Handle ingestion navigation prompt buttons
      if (actionId === 'navigate-to-ingestion') {
        const entry = this._pendingIngestions.get(messageId)
        if (entry) {
          this.client?.controller.updateMessage(messageId, { used: true })
          this._pendingIngestions.delete(messageId)
          this.client?.controller.addAgentMessage(
            'Taking you to the ingestion review. You can match this quote to a structure there.',
          )
          this.isOpen.set(false)
          this.router.navigateByUrl(entry.url)
        }
        return
      }
      if (actionId === 'continue-working') {
        const entry = this._pendingIngestions.get(messageId)
        this.client?.controller.updateMessage(messageId, { used: true })
        this._pendingIngestions.delete(messageId)
        this.client?.controller.addAgentMessage(
          entry?.matched
            ? 'Quote saved! You can review it anytime from the ingestion review. What else can I help you with?'
            : 'No problem — the quote was not saved. What else can I help you with?',
        )
        return
      }

      const msg = this.client?.controller.getMessage(messageId)
      if (!msg || msg.type !== 'action' || msg.used) return
      const state = this.client!.getState()
      if (state !== 'idle' && state !== 'error') return
      const label = msg.actions.find((a) => a.id === actionId)?.label ?? actionId
      this.client!.controller.updateMessage(messageId, { used: true })
      this.sendAsUserMessage(label)
    }).onDropdownSubmit((messageId, choices) => {
      const msg = this.client?.controller.getMessage(messageId)
      if (!msg || msg.type !== 'dropdown' || msg.selectedChoices) return
      const state = this.client!.getState()
      if (state !== 'idle' && state !== 'error') return
      const text = Object.entries(choices)
        .map(([key, value]) => {
          const field = msg.fields.find((f) => f.key === key)
          const option = field?.options.find((o) => o.value === value)
          return `${field?.label ?? key}: ${option?.label ?? value}`
        })
        .join('; ')
      if (!text) return
      this.client!.controller.updateMessage(messageId, { selectedChoices: choices })
      this.sendAsUserMessage(text)
    })
  }

  private sendAsUserMessage(text: string): void {
    const controller = this.client!.controller
    controller.addUserMessage(text)
    controller.setDisabled(true)
    controller.setShowInterrupt(true)
    void this.client!.sendMessage(text).catch((err: unknown) => {
      controller.addErrorMessage(err instanceof Error ? err.message : String(err))
      controller.setDisabled(false)
      controller.setShowInterrupt(false)
    })
  }

  private async handleEmailIngestion(
    message: string,
    emailFiles: Array<{ file: File; name: string; size: number }>,
  ): Promise<void> {
    const controller = this.client!.controller
    const fileNames = emailFiles.map((f) => f.name).join(', ')
    controller.addUserMessage(
      message || `Processing ${fileNames}...`,
      emailFiles.map((f, i) => ({ ...f, id: `email-${i}`, type: f.name.split('.').pop() ?? '' })),
    )
    controller.setDisabled(true)

    let lastEmailId: number | null = null
    let lastMatched = false

    const quoteFileExts = new Set(['msg', 'eml', 'xlsx', 'xls', 'xlsm', 'xlsb', 'csv'])

    for (const attachment of emailFiles) {
      const ext = attachment.name.toLowerCase().split('.').pop() ?? ''
      if (!quoteFileExts.has(ext)) {
        controller.addErrorMessage(
          `"${attachment.name}" is not a supported file type. Accepted: ${[...quoteFileExts].map((x) => '.' + x).join(', ')}`,
        )
        continue
      }
      if (attachment.size === 0) {
        controller.addErrorMessage(`"${attachment.name}" is empty. Please attach a valid file.`)
        continue
      }

      const isSpreadsheet = ['xlsx', 'xls', 'xlsm', 'csv'].includes(ext)
      const loadingSteps = isSpreadsheet
        ? ['Reading spreadsheet...', 'Extracting quote data...', 'Creating pending quotes...']
        : ['Parsing email...', 'Extracting quote data...', 'Creating pending quotes...']

      const loadingId = controller.addIndeterminateMessage(
        `Processing ${attachment.name}...`,
        loadingSteps,
        3000,
      )

      try {
        const token = await this.authService.getAccessToken()
        const formData = new FormData()
        formData.append('file', attachment.file, attachment.name)

        const url = `${environment.internalApi.base}/ai-quote-responses/ingest-email`
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        })

        if (!response.ok) {
          const errorBody = await response.text()
          let errorMsg = `Server error (${response.status})`
          try {
            const parsed = JSON.parse(errorBody)
            errorMsg = parsed.Message || parsed.message || errorMsg
          } catch {
            /* use default */
          }
          throw new Error(errorMsg)
        }

        const result = await response.json()

        if (result.id && result.pendingQuotes) {
          this.store.dispatch(IngestionReviewActions.injectIngestion({ ingestion: result }))
        } else if (result.emailId != null) {
          this.store.dispatch(
            IngestionReviewActions.injectIngestion({
              ingestion: {
                id: result.emailId,
                fromAddresses: '',
                toAddresses: '',
                ccAddresses: null,
                subject: attachment.name,
                body: '',
                auditInsertDate: new Date().toISOString(),
                structureDetail: null as any,
                pendingQuotes: [],
                status: QuoteIngestionStatus.Pending,
              },
            }),
          )
        }

        if (loadingId) {
          controller.removeMessage(loadingId)
        }

        if (result.structureDetail) {
          lastEmailId = result.emailId ?? result.id ?? lastEmailId
          lastMatched = true
          controller.addAgentMessage(`${attachment.name} — quote extracted successfully!`)
        } else {
          lastEmailId = result.emailId ?? result.id ?? lastEmailId
          lastMatched = false
          controller.addErrorMessage(
            `${attachment.name} — quote could not be matched to a program or structure. You can map it manually in the review screen.`,
          )
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to process quote email.'
        if (loadingId) {
          controller.removeMessage(loadingId)
        }
        controller.addErrorMessage(`${attachment.name} — ${errorMessage}`)
      }
    }

    controller.setDisabled(false)

    if (lastEmailId != null) {
      this._lastIngestedEmailId = lastEmailId
      this.contextSnapshot.openEmailId = lastEmailId.toString()
      this.navigateOrPromptIngestion(
        this.router.serializeUrl(
          this.router.createUrlTree(['/quote/ai-quote-ingestion'], {
            queryParams: { openEmailId: lastEmailId },
          }),
        ),
        lastMatched,
      )
    }
  }

  private looksLikeQuote(text: string): boolean {
    return (
      ChatWindowOverlayComponent.QUOTE_KEYWORDS.filter((kw) =>
        new RegExp(`\\b${kw.replace(/\s+/g, '\\s+')}\\b`, 'i').test(text),
      ).length >= ChatWindowOverlayComponent.QUOTE_KEYWORD_THRESHOLD
    )
  }

  private extractStructureId(text: string): number | null {
    for (const pattern of ChatWindowOverlayComponent.STRUCTURE_ID_PATTERNS) {
      const match = text.match(pattern)
      if (match) {
        const parsed = parseInt(match[1], 10)
        if (!isNaN(parsed) && parsed > 0) {
          return parsed
        }
      }
    }
    return null
  }

  private async handleQuoteTextIngestion(message: string): Promise<void> {
    const controller = this.client!.controller
    controller.addUserMessage(message)
    controller.setDisabled(true)

    const structureId = this.extractStructureId(message)

    const loadingId = controller.addIndeterminateMessage(
      'Processing pasted quote text...',
      ['Detecting quote data...', 'Extracting terms...', 'Creating pending quotes...'],
      3000,
    )

    try {
      const token = await this.authService.getAccessToken()
      const env = environment.genAI.ingestionReview
      const url = `${environment.internalApi.base}${env.emails}${env.chatIngest}`

      const requestBody: Record<string, unknown> = { emailBody: message }
      if (structureId !== null) {
        requestBody['structureId'] = structureId
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorBody = await response.text()
        let errorMsg = `Server error (${response.status})`
        try {
          const parsed = JSON.parse(errorBody)
          errorMsg = parsed.Message || parsed.message || errorMsg
        } catch {
          /* use default */
        }
        throw new Error(errorMsg)
      }

      const result = await response.json()
      const emailId: number | null = result.emailId ?? result.id ?? null

      // Inject into store for optimistic UI
      if (result.id && result.pendingQuotes) {
        this.store.dispatch(IngestionReviewActions.injectIngestion({ ingestion: result }))
      } else if (emailId !== null) {
        this.store.dispatch(
          IngestionReviewActions.injectIngestion({
            ingestion: {
              id: emailId,
              fromAddresses: '',
              toAddresses: '',
              ccAddresses: null,
              subject: '(Pasted quote text)',
              body: message,
              auditInsertDate: new Date().toISOString(),
              structureDetail: null as any,
              pendingQuotes: [],
              status: QuoteIngestionStatus.Pending,
            },
          }),
        )
      }

      if (loadingId) {
        controller.removeMessage(loadingId)
      }

      if (result.structureDetail) {
        controller.addAgentMessage('Quote extracted successfully!')
      } else if (structureId !== null) {
        controller.addErrorMessage(
          `Quote extracted, but structure ID ${structureId} could not be matched. You can map it manually in the review screen.`,
        )
      } else {
        controller.addErrorMessage(
          'Quote extracted — could not match to a structure. You can map it manually in the review screen.',
        )
      }

      controller.setDisabled(false)

      if (emailId !== null) {
        this._lastIngestedEmailId = emailId
        this.contextSnapshot.openEmailId = emailId.toString()
        this.navigateOrPromptIngestion(
          this.router.serializeUrl(
            this.router.createUrlTree(['/quote/ai-quote-ingestion'], {
              queryParams: { openEmailId: emailId },
            }),
          ),
          !!result.structureDetail,
        )
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to process quote text.'
      if (loadingId) {
        controller.removeMessage(loadingId)
      }
      controller.addErrorMessage(`Quote ingestion failed: ${errorMessage}`)
      controller.setDisabled(false)
    }
  }

  private navigateOrPromptIngestion(url: string, matched: boolean): void {
    const controller = this.client?.controller
    if (!controller) return

    if (window.location.pathname.includes('/quote/ai-quote-ingestion')) {
      this.isOpen.set(false)
      this.router.navigateByUrl(url)
      return
    }
    const msgId = controller.addActionMessage('Quote processed! Would you like to review it now?', [
      { id: 'navigate-to-ingestion', label: 'View Quote', variant: 'primary' },
      { id: 'continue-working', label: 'Continue', variant: 'secondary' },
    ])
    this._pendingIngestions.set(msgId, { url, matched })
  }

  ngOnDestroy(): void {
    this.contextSub?.unsubscribe()
    this.deepLinkSub?.unsubscribe()
    this.keyboardListeners.forEach((remove) => remove())
    this.client?.destroy()
    if (document.body.contains(this.el.nativeElement)) {
      document.body.removeChild(this.el.nativeElement)
    }
  }

  toggleOpen(): void {
    this.isOpen.update((v) => !v)
    if (this.isOpen()) this.greetIfNeeded()
  }

  open(): void {
    this.isOpen.set(true)
    this.greetIfNeeded()
  }

  close(): void {
    this.isOpen.set(false)
  }

  private greetIfNeeded(): void {
    const messages = this.client?.controller.getMessages() ?? []
    if (
      messages.some(
        (m) => m.type === 'text' && m.sender !== 'user' && m.content.includes(GREETING_MARKER),
      )
    ) {
      return
    }
    const firstName = this.resolveFirstName(this.contextSnapshot['user'])
    const greeting = firstName ? `Hi ${firstName}! ${GREETING_MARKER}` : `Hi! ${GREETING_MARKER}`
    this.client?.controller.addAgentMessage(greeting)
  }

  private resolveFirstName(user: string | undefined): string | null {
    if (!user) return null
    // "LastName, FirstName" display-name format used by Azure AD
    if (user.includes(',')) return user.split(',')[1]?.trim() || null
    // Email address — "kiryl.baravikou@…" → "Kiryl"
    if (user.includes('@')) {
      const first = user.split('@')[0].split('.')[0]
      return first ? first.charAt(0).toUpperCase() + first.slice(1) : null
    }
    // "FirstName LastName"
    return user.split(' ')[0] || null
  }

  private draftRouteFromUrl(url: string | undefined): string | null {
    const match = url?.match(/\/draft\/\d+\/([^/?]+)/)
    return match ? match[1] : null
  }

  private extractScreenContent(): string {
    const container = document.querySelector('mat-sidenav-content')
    if (!container) return ''

    const segments: string[] = []

    for (const [selector, label] of [
      ['app-layer-list', 'Layers'],
      ['app-properties-panel', 'Layer Properties'],
    ] as [string, string][]) {
      const el = document.querySelector(selector)
      if (!el) continue
      const text = (el as HTMLElement).innerText.replace(/\s+/g, ' ').trim()
      if (text) segments.push(`[${label}] ${text}`)
    }

    const fieldPairs: string[] = []
    const roots = [
      container,
      document.querySelector('app-layer-list'),
      document.querySelector('app-properties-panel'),
    ].filter(Boolean) as Element[]
    for (const root of roots) {
      root.querySelectorAll('mat-form-field').forEach((field) => {
        const label = field.querySelector('mat-label, label')?.textContent?.trim() ?? ''
        const input = field.querySelector('input, textarea') as HTMLInputElement | null
        const selectText =
          field
            .querySelector('.mat-mdc-select-value-text, .mat-select-value-text')
            ?.textContent?.trim() ?? ''
        const value = input?.value?.trim() || selectText
        if (label && value) fieldPairs.push(`${label}: ${value}`)
      })
    }

    container.querySelectorAll('[id^="metric-settings-"] > div.row > div').forEach((row) => {
      const toggle = row.querySelector('mat-slide-toggle') as HTMLElement | null
      const weightInput = row.querySelector('input[type="number"]') as HTMLInputElement | null
      const label = (row.querySelector('label') as HTMLElement)?.innerText?.trim()
      const enabled =
        toggle?.classList.contains('mat-mdc-slide-toggle-checked') ||
        toggle?.querySelector('button[role="switch"]')?.getAttribute('aria-checked') === 'true' ||
        false
      const weight = weightInput?.value?.trim() ?? ''
      if (label)
        fieldPairs.push(
          `${label}: ${enabled ? 'enabled' : 'disabled'}${weight ? `, weight: ${weight}` : ''}`,
        )
    })

    for (const root of roots) {
      root.querySelectorAll('mat-slide-toggle:not(.check), mat-checkbox').forEach((toggle) => {
        const input = toggle.querySelector('input[type="checkbox"]') as HTMLInputElement | null
        const checked = input?.checked ?? toggle.classList.contains('mat-checked') ?? false
        const label = (
          toggle.querySelector('.mdc-label, .mat-slide-toggle-content, label') as HTMLElement
        )?.innerText?.trim()
        if (label) fieldPairs.push(`${label}: ${checked ? 'enabled' : 'disabled'}`)
      })
    }

    if (fieldPairs.length > 0) segments.push('[Field values] ' + fieldPairs.join(' | '))

    container.querySelectorAll('mat-expansion-panel').forEach((panel) => {
      const header = (panel.querySelector('mat-expansion-panel-header') as HTMLElement)?.innerText
        ?.replace(/\s+/g, ' ')
        .trim()
      const body = panel
        .querySelector('.mat-expansion-panel-body')
        ?.textContent?.replace(/\s+/g, ' ')
        .trim()
      if (header && body) segments.push(`[${header}] ${body}`)
    })

    const general = (container as HTMLElement).innerText.replace(/\s+/g, ' ').trim()
    segments.push(general)

    return segments.join(' | ').substring(0, 4000)
  }
}
